// Search-filterable multi-select dropdown — same look/wiring as `Combobox`,
// but selecting an option doesn't close the popover (checkbox-style picks)
// and the trigger shows a count/summary instead of a single label.
import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "lib/utils";
import { Popover, PopoverTrigger, PopoverContent } from "ui/popover";
import { Input } from "ui/input";
import { Checkbox } from "ui/checkbox";

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface MultiComboboxProps {
  id?: string;
  options: ComboboxOption[];
  value?: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

export function MultiCombobox({
  id,
  options,
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  disabled,
  className,
}: MultiComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const selected = value || [];
  const selectedLabels = options.filter((o) => selected.includes(o.value)).map((o) => o.label);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (optionValue: string) => {
    if (selected.includes(optionValue)) {
      onValueChange(selected.filter((v) => v !== optionValue));
    } else {
      onValueChange([...selected, optionValue]);
    }
  };

  const triggerLabel =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
      ? selectedLabels[0]
      : `${selectedLabels.length} selected`;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-left text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span className={cn("line-clamp-1", selectedLabels.length === 0 && "text-muted-foreground")}>
            {triggerLabel}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-0"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <div className="p-2">
          <Input
            ref={searchInputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1 pt-0">
          {filtered.length === 0 ? (
            <p className="px-2 py-3 text-center text-sm text-muted-foreground">{emptyText}</p>
          ) : (
            filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                className="flex w-full cursor-pointer select-none items-center gap-2 rounded-md py-1.5 px-2 text-left text-sm outline-none hover:bg-muted focus:bg-muted focus:text-foreground"
              >
                <Checkbox checked={selected.includes(o.value)} className="pointer-events-none" />
                {o.label}
              </button>
            ))
          )}
        </div>
        {selected.length > 0 && (
          <div className="flex items-center justify-between border-t p-2">
            <span className="text-xs text-muted-foreground">{selected.length} selected</span>
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              onClick={() => onValueChange([])}
            >
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
